/** @module csi/slicer/slicerExcelController */
define([
	"dojo/_base/declare",
	"dojo/dom-construct",
	"csi/_base/underscore",
	"csi/action/Controller",
	"csi/comm/PostRequest",
	"csi/html",
	"csi/locale",
	"csi/state",
	"csi/types"
], function(declare, domConstruct, _, Controller, PostRequest, html, locale, state, types) {

	/**
	 * The purpose of this module - take slicer columns, rows and data from colTree and treeData objects (see csi/slicer/slicerCalc.js for objects description)
	 * and export slicer data to Excel format, preserving rows, columns and cell formatting.
	 * 
	 * On UI we prepare data and send prepared to the server. Server create actual Excel file and return it to UI.
	 * 
	 * Data that we prepare should be in the form of HTML table with inline styling. CSS classes should not be added for formatting purposes (server will ignore most css classes)
	 * instead, inline styling should be used. However, some classes might be used, but not in the means of node formatting , but to deliver some options to the server.
	 * For instance, adding css class "csi-slicer-metric" to the cell will tell the  server to treat cell value as number (otherwise it will be string and 
	 * excel treat it as string) 
	 * 
	 * Following inline styles are supported by server (as of today, August 22, 2017)
	 * ----------------------------------
	 * STYLE				NOTES
	 * text-align			Valid values: center, left, right
	 * background-color		Must be rgb or rgba
	 * background-image		It must be ‘linear-gradient’ with 2 RGB colors.  For example: linear-gradient(rgb(0,0,0),rgb(255,255,255))
	 * color				Must be rgb or rgba
	 * font-size			Must ends with ‘pt’. For example: 4pt
	 * font-weight			Only ‘bold’ is processed
	 * ----------------------------------
	 * 
	 * For formatted metrics we need to specify attribute "data-excel-format" on "<td>" tag. The format is described here (excel format specification):
	 * https://support.office.com/en-us/article/Create-or-delete-a-custom-number-format-78f2a361-936b-4c03-8772-09fab54be7f4
	 * The number itself MUST NOT be formatted in this case.
	 * 
	 * Created table should then be submitted to the server as form, using method POST 
	 * 
	 * <html xmlns:msxsl="urn:schemas-microsoft-com:xslt">
	 * 
	 * @Example
	 * Example of prepared HTML Table:
	 * 
	 *    <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
	 *    <body>
	 *       <table>
	 *          <tr style="background-color: rgb(243, 243, 243); text-align: center; font-weight: bold;">
	 *             <th></th>
	 *             <th></th>
	 *             <th colspan="4">Metrics</th>
	 *          </tr>
	 *          <tr style="background-color: rgb(243, 243, 243); text-align: center; font-weight: bold;">
	 *             <th>Brand</th>
	 *             <th>Style</th>
	 *             <th colspan="1">TargetRevenue</th>
	 *             <th colspan="1">Target Cost</th>
	 *             <th colspan="1">TargetCostPct</th>
	 *             <th colspan="1">Count</th>
	 *          </tr>
	 *          <tr style="font-weight: bold;">
	 *             <td> Kids</td>
	 *             <td></td>
	 *             <td  data-excel-format="$#,##0.00" class="csi-slicer-metric">1071000</td>
	 *             <td  class="csi-slicer-metric">2400</td>
	 *             <td  data-excel-format="0.00%" class="csi-slicer-metric">1</td>
	 *             <td  class="csi-slicer-metric">3</td>
	 *          </tr>
	 *          <tr >
	 *             <td></td>
	 *             <td> Santorini</td>
	 *             <td style="background-color:rgb(250, 128, 128);color:rgb(255, 255, 255)" data-excel-format="$#,##0.00" class="csi-slicer-metric">1071000</td>
	 *             <td style="background-color:rgb(203, 234, 170)" class="csi-slicer-metric">2400</td>
	 *             <td  data-excel-format="0.00%" class="csi-slicer-metric">1</td>
	 *             <td  class="csi-slicer-metric">3</td>
	 *          </tr>
	 *          <tr style="background-color: rgb(243, 243, 243); font-weight: bold;">
	 *             <td>Grand Totals</td>
	 *             <td></td>
	 *             <td  data-excel-format="$#,##0.00" class="csi-slicer-metric">1071000</td>
	 *             <td  class="csi-slicer-metric">2400</td>
	 *             <td  data-excel-format="0.00%" class="csi-slicer-metric">1</td>
	 *             <td  class="csi-slicer-metric">3</td>
	 *          </tr>
	 *          <tr>
	 *             <td colspan="6">Values updated as of 10/20/17, 1:00 AM</td>
	 *          </tr>
	 *       </table>
	 *    </body>
	 * </html>
	 * 
	 */

	// header style
	var headerStyle = 'style="background-color: rgb(243, 243, 243); text-align: center; font-weight: bold;"';
	var grandTotalStyle = 'style="background-color: rgb(243, 243, 243); font-weight: bold;"';
	var subTotalStyle = 'style="font-weight: bold;"';
	var US_DECIMAL = ".";

	var cssCache = {}; // Cache for used css classes

	/**
	 * Create hidden input and add form parameter to it
	 * @param {DomNode} form
	 * @param {string} key
	 * @param {string} value
	 * @return {DomNode}
	 */
	function addParameterToForm(form, key, value) {
		return html.create("input", {type: "hidden", name: key, value: value}, form);
	}

	/**
	 * Add style parameter to the style object. basically just save it for future use
	 * @param {Object} cellStyle
	 * @param {string} name
	 * @param {Object} classStyle = css.style object, contains all definition for the given class
	 */
	function addStyle(cellStyle, name, classStyle) {
		var value = classStyle && classStyle[name];
		if (value) {
			cellStyle[name] = value;
		}
	}

	/**
	 * Create style="..." string, using style object, created with function addStyle()
	 * @param {Object} cellStyle
	 * @return {string} - style="..." string
	 */
	function createStyle(cellStyle) {
		var res = '';
		if (!_.isEmpty(cellStyle)) {
			_.each(cellStyle, function(val, key) {
				res += (res ? ';' : "") + key + ':' + val;
			});
			if (res) {
				res = 'style="' + res + '"';
			}
		}
		return res;
	}

	/**
	 * Scan all css sheets for given document, finding specified class. This is very basic function, does not do any aggregation.
	 * Function save found values in the cache object cssCache 
	 * @param {string} className
	 */
	function getCssClass(className) {
		className = className.toLowerCase();

		var myClass = cssCache[className];
		if (myClass != null) { // Have in cache?
			return myClass;
		}
		var classNameAlt = className.substr(0, 1) == '.' ? className.substr(1) : '.' + className;

		for (var i = 0; i < document.styleSheets.length; i++) {
			var mysheet = document.styleSheets[i];
			var myrules = mysheet.cssRules ? mysheet.cssRules : mysheet.rules;
			for (var j = 0; j < myrules.length; j++) {
				if (myrules[j].selectorText) {
					var check = myrules[j].selectorText.toLowerCase();
					switch (check) {
						case className:
						case classNameAlt:
							var res = myrules[j].style; // caching
							cssCache[className] = res;
							return res;
							break;
					}
				}
			}
		}
		// if class is not defined in css - remember it and do not search for it again
		cssCache[className] = "";
	}

	/**
	 * Replace &nbsp with normal space
	 * @param {string} txt
	 * @return {string}
	 */
	function replaceNbsp(txt) {
		return txt.replace(String.fromCharCode(160), " ");
	}

	return declare(Controller, {
		/**
		 * Handle error and confirmation messages
		 */
		preProcess: function() {
			this.setProgress();
			this.reportStartOfExportToServer();

			html.showWait(true);

			var self = this;
			_.delay(function() { // allow browser to take a breath and update page, show wait cursor, don't look stall.
				self.publish();
			}, 150); // we have transition time set to 100ms, we have to wait longer here, otherwise wait cursor will not appear.
		},

		/**
		 * Let the server know some additional information before we Level =  to export
		 */
		reportStartOfExportToServer: function() {
			var params = this._getLogParams();
			var request = new PostRequest("SiteAdmin", "ConvertHTMLToExcelStarted", params);
			request.go();
		},

		/**
		 * Get information to pass to the server to be written to the server log
		 */
		_getLogParams: function() {
			var params = {
				CustomViewURL: this.widget.get("customViewId"),
				ViewNode: this.widget.getViewURL(),
				Locale: locale.getLocale()
			};

			var defView = this.widget.defView;
			if (defView) {
				params.ViewId = defView.getId();
				params.ViewName = defView.getName();
			}

			return params;
		},

		/**
		 * Parse compound columns, gather information for table header. Gathered information being stored in colId array.
		 * @param {string[][]} header - two dimensional array of header cells: column, row 
		 * @param {Object{ columns - dgrid's columns object
		 * @param {number} level - current level in recursion
		 * @param {Object[]} colId - {field:{number}, f:{function //column's renderHeaderCell}, isTotal:{boolean}, isCompare:{boolean}}
		 */
		parseHeader: function(header, columns, level, colId) {
			const cfg = this.widget._slicer._config;
			const startLevel = cfg.flatSlicer ? 1 : 0;
			if (level > startLevel) { // skip root cell with colClsNames
				var node = domConstruct.create('div');
				node.columnId = columns.id;
				columns.renderHeaderCell(node);
				const colSpan = columns.headerNode.colSpan;
				const rowSpan = (columns.field == "rowCls") ?  header.length : 1;
				var format = columns.spec && columns.spec.format;
				if (format && (format.indexOf(US_DECIMAL) == (format.length - 1))) {
					format = format.substring(0, format.length - 1);
				}

				var txt = _.escape(replaceNbsp(node.innerText));
				header[level - startLevel * 2] += '<th colspan="' + colSpan + '" rowspan="'+ rowSpan + '">' + txt + '</th>';
				if (columns.field != null) {
					colId.push({field: columns.field, f: columns.renderCell, isTotal: columns.isTotal, isCompare: columns.isCompare, format: format});
				}
			}
			_.each(columns.children, function(column) {
				if (column.isDummy) {
					return;
				}
				this.parseHeader(header, column, level + 1, colId);
			}, this);
		},

		/**
		 * Create table body
		 * @param {TreeData} treeData - see slicerCalc for object description and details
		 * @param {number} treeDepth - how many levels have 1st colum's tree
		 * @param {Object[]} colId - {field:{number}, f:{function //column's renderHeaderCell}, isTotal:{boolean}, isCompare:{boolean}}
		 * @param {number} level - current level in recursion
		 * @param {function} f - slicer._renderCell() function (function pointer taken from columns structure) ƒ(object, value, td, options)
		 * @param {string} rowClsName - name of the current row, name was calculated on the previous level of recursion. On level 0 name is undefined.
		 * @return {string} - return table body html string 
		 */
		parseBody: function(treeData, treeDepth, colId, level, f, rowClsName) {
			function createRow() {
				var style = '';
				var isGrandTotal = (level == 0);
				var isSubTotal = ((level > 0) && (level < treeDepth));
				if (isGrandTotal) {
					style = grandTotalStyle;
				} else if (isSubTotal) {
					style = subTotalStyle;
				}
				var body1 = '<tr ' + style + '>';
				var pos = (level == 0) ? 0 : level - 1;
				for (var i = 0; i < pos; i++) {
					body1 += '<td></td>';
				}
				var body3 = '';
				for (i = pos; i < treeDepth - 1; i++) {
					body3 += '<td></td>';
				}
				var row = body1 + '<td>' + (level == 0 ? locale.resource('SlicerGrandTotal') : rowClsName) + '</td>' + body3;
				for (i = 0; i < colId.length; i++) {
					var value = treeData[colId[i].field];
					var node = domConstruct.create('div');
					var options = {isTotal: colId[i].isTotal, isCompare: colId[i].isCompare};
					colId[i].f(obj, value, node, options); // call column render function

					var cellStyle = {};

					_.each(node.classList, function(className) {
						var classStyle = getCssClass(className);
						if (classStyle) {
							if (!isGrandTotal && !isSubTotal) { // Grand Total style overrides these settings
								addStyle(cellStyle, 'font-weight', classStyle);
								addStyle(cellStyle, 'background-color', classStyle);
							}
							addStyle(cellStyle, 'color', classStyle);
						}
					});
					var txt = node.innerText;
					var format = colId[i].format;
					var dataFormat = '';
					if (format) {
						txt = (value == undefined) ? '' : value;
						dataFormat = ' data-excel-format="' + _.escape(format) + '"';
					}
					txt = _.escape(txt);
					const isDate = types.isTimeFormat(format);
					if (isDate) {
						txt = types.formatTime(txt, format);
					}
					// adding css class "csi-slicer-metric" to the cell will tell the  server to treat cell value as 
					// number (otherwise it will be string and excel treat it as string)
					row += '<td ' + createStyle(cellStyle) + dataFormat + ' class="csi-slicer-metric' + (isDate ? ' attrDate' : '') + '">' + txt + '</td>';
				}
				return row + '</tr>';
			}

			var body = '';
			var obj = {level: level, isMetricsRow: (level == treeDepth)};

			var row = createRow();
			if (level > 0) { // not grand total?
				body += row;
			}

			_.each(treeData, function(data, key) {
				if (_.isObject(data)) {
					var node = domConstruct.create('div');
					f(obj, key, node, null);
					var nextName = _.escape(node.innerText);

					body += this.parseBody(data, treeDepth, colId, level + 1, f, nextName);
				}
			}, this);

			if (level == 0) { // grand total?
				body += row;
			}
			return body;
		},

		/**
		 * Main function: Create html table with all header, data and styles 
		 * @return {string}
		 */
		_createTable: function() {
			var slicer = this.widget._slicer;
			var colData = slicer.colData;
			var cfg = slicer._config;
			var colId = []; // array of column objects: {field:{number}, f:{function //column's renderHeaderCell}, isTotal:{boolean}, isCompare:{boolean}}
			var treeDepth = cfg.rowClassifiers.length; // depth of the left column tree

			var headerDepth = cfg.columnClassifiers.length + 1; // number of header rows: column classifiers + metrics row;
			var header = []; // two dimensional array of header cells: column, row

			// header cells for row classifiers names
			var treeClassifiers = ''; // cells with row classifiers names
			var emptyCells = ''; // empty rows atop of cells with row classifiers names
			for (var i = 0; i < cfg.rowClassifiers.length; i++) {
				var txt = _.escape(cfg.classifierStore.query({id: cfg.rowClassifiers[i]})[0].name);
				treeClassifiers += '<th>' + txt + '</th>';
				emptyCells += '<th></th>';
			}

			// create string with column classifiers names for upper header line
			var colClsNames = '';
			var metricsPos = cfg.metricsPos;
			var colClsLen = cfg.columnClassifiers.length;
			for (i = 0; i < headerDepth; i++) {
				var name;
				if (((metricsPos >= colClsLen) && (i == colClsLen)) || (i == metricsPos)) {
					name = locale.resource("SlicerMetrics");
				} else {
					var pos = (i > metricsPos) ? i - 1 : i;
					var data = slicer.colClsNamesStore.query({id: cfg.columnClassifiers[pos]});
					name = data.length ? data[0].name : cfg.columnClassifiers[pos];
				}
				name = _.escape(name);
				colClsNames += (colClsNames ? ' / ' : '') + name;
			}
			// insert upper lane for compound column (whole row). colData.columns format: [[[treeColumn]],[[compoundColumns]]]
			var colSpan0 = colData.columns[1][0][0].colSpan - 1;// it is -1 because of dummy column 
			header[0] = '<tr ' + headerStyle + '>' + emptyCells + '<th colspan="' + colSpan0 + '">' + colClsNames + '</th>';
			// insert compound header columns (only left part of the row)
			for (i = 1; i <= headerDepth - 1; i++) {
				header[i] = '<tr ' + headerStyle + '>' + emptyCells;
			}
			// insert row classifiers row (only left part of the row)
			header[headerDepth] = '<tr ' + headerStyle + '>' + treeClassifiers;

			// add compound column
			this.parseHeader(header, colData.columns[1][0][0], 0, colId);

			// output buffer
			var tableContent = "<table>";

			// add header rows to output buffer and close rows tr tag
			for (i = 0; i <= headerDepth; i++) {
				tableContent += header[i] + '</tr>';
			}

			// generate body rows (leaves and sub totals)
			var tableBody = this.parseBody(colData.treeData, treeDepth, colId, 0, colData.columns[0][0][0].renderCell);

			// generate date row
			var date = _.escape(locale.resource("SLICER_DATA_DATE", [slicer.store.getDate()]));
			date = '<tr><td colspan="' + (treeDepth + colSpan0) + '">' + date + '</td></tr>';

			cssCache = {}; // clear cache, return used memory

			return tableContent + tableBody + date + "</table>";
		},

		/**
		 * Create flat slicer table body
		 * @param {TreeData} treeData - see slicerCalc for object description and details
		 * @param {number} treeDepth - how many levels have 1st colum's tree
		 * @param {Object[]} colId - {field:{number}, f:{function //column's// renderHeaderCell}, isTotal:{boolean}, isCompare:{boolean}}
		 * @param {number} level - current level in recursion
		 * @param {function} f - slicer._renderCell() function (function pointer taken from columns structure) ƒ(object, value, td, options)
		 * @param {string} rowClsName - name of the current row, name was calculated on the previous level of recursion. On level 0 name is undefined.
		 * @param {string[]} rowClsNames - array of row classifiers values names per column
		 * @return {string} - return table body html string 
		 */
		parseFlatBody: function(treeData, treeDepth, colId, level, f, rowClsName, rowClsNames) {
			function createRow() {
				let style = '';
				const isGrandTotal = (level == 0);
				const isSubTotal = ((level > 0) && (level < treeDepth));
				if (isGrandTotal) {
					style = grandTotalStyle;
				} else if (isSubTotal) {
					style = subTotalStyle;
				}
				var body1 = '<tr ' + style + '>';
				var pos = (level == 0) ? 0 : level - 1;
				for (var i = 0; i < pos; i++) {
					body1 += '<td>' + rowClsNames[i] + '</td>';
				}
				var body3 = '';
				for (i = pos; i < treeDepth - 1; i++) {
					body3 += '<td>' + "" + '</td>';
				}
				var row = body1 + '<td>' + (level == 0 ? locale.resource('SlicerGrandTotal') : rowClsName) + '</td>' + body3;
				for (i = treeDepth; i < colId.length; i++) {
					var value = treeData[colId[i].field];
					var node = domConstruct.create('div');
					node.columnId = i.toString();
					var options = {isTotal: colId[i].isTotal, isCompare: colId[i].isCompare};
					colId[i].f(obj, value, node, options); // call column render function

					var cellStyle = {};

					_.each(node.classList, function(className) {
						var classStyle = getCssClass(className);
						if (classStyle) {
							if (!isGrandTotal && !isSubTotal) { // Grand Total style overrides these settings
								addStyle(cellStyle, 'font-weight', classStyle);
								addStyle(cellStyle, 'background-color', classStyle);
							}
							addStyle(cellStyle, 'color', classStyle);
						}
					});
					var txt = node.innerText;
					var format = colId[i].format;
					var dataFormat = '';
					if (format) {
						txt = (value == undefined) ? '' : value;
						dataFormat = ' data-excel-format="' + _.escape(format) + '"';
					}
					txt = _.escape(txt);
					const isDate = types.isTimeFormat(format);
					if (isDate) {
						txt = types.formatTime(txt, format);
					}
					// adding css class "csi-slicer-metric" to the cell will tell the  server to treat cell value as 
					// number (otherwise it will be string and excel treat it as string)
					row += '<td ' + createStyle(cellStyle) + dataFormat + ' class="csi-slicer-metric' + (isDate ? ' attrDate' : '') + '">' + txt + '</td>';
				}
				return row + '</tr>';
			}

			var body = '';
			var obj = {level: level, isMetricsRow: (level == treeDepth)};

			var row = createRow();
			if (level == treeDepth) { // not totals?
				body += row;
			}

			_.each(treeData, function(data, key) {
				if (_.isObject(data)) {
					var node = domConstruct.create('div');
					node.columnId = level.toString();
					f(obj, key, node, {bExcel: true});
					var nextName = _.escape(node.innerText);
					rowClsNames[level] = nextName;

					body += this.parseFlatBody(data, treeDepth, colId, level + 1, f, nextName, rowClsNames);
				}
			}, this);

			if (level < treeDepth) { // grand total or first total?
				body += row;
			}
			return body;
		},

		/**
		 * FlatSlicer: Create html table with all header, data and styles 
		 * @return {string}
		 */
		_createFlatTable: function() {
			var slicer = this.widget._slicer;
			var colData = slicer.colData;
			var cfg = slicer._config;
			var colId = []; // array of column objects: {field:{number}, f:{function //column's renderHeaderCell}, isTotal:{boolean}, isCompare:{boolean}}
			var treeDepth = cfg.rowClassifiers.length; // depth of the left column tree

			var headerDepth = cfg.columnClassifiers.length + 1; // number of header rows: column classifiers + metrics row;
			var header = []; // two dimensional array of header cells: column, row

			for (i = 0; i <= headerDepth - 1; i++) {
				header[i] = '<tr ' + headerStyle + '>';
			}

			// add compound column
			this.parseHeader(header, colData.columns[0][0][0], 0, colId);

			// output buffer
			var tableContent = "<table>";

			const rowClsColSpan = colData.columns[0][0][0].children[0].colSpan;
			const colClsColSpan = colData.columns[0][0][0].children[1].colSpan;
			const totalColSpan = rowClsColSpan + colClsColSpan;

			// skip header for row classifier - no need to duplicate them 
			tableContent += '<tr>';
			for (i = 0; i < rowClsColSpan; i++ ){ // don't use "<td colspan=rowClsColSpan>..." as excel will ignore it
				tableContent += '<td></td>';
			}

			// add classifiers names 
			var colClsNames = '';
			var metricsPos = cfg.metricsPos;
			var colClsLen = cfg.columnClassifiers.length;
			for (i = 0; i < headerDepth; i++) {
				var name;
				if (((metricsPos >= colClsLen) && (i == colClsLen)) || (i == metricsPos)) {
					name = locale.resource("SlicerMetrics");
				} else {
					var pos = (i > metricsPos) ? i - 1 : i;
					var data = slicer.colClsNamesStore.query({id: cfg.columnClassifiers[pos]});
					name = data.length ? data[0].name : cfg.columnClassifiers[pos];
				}
				name = _.escape(name);
				colClsNames += (colClsNames ? ' / ' : '') + name;
			}
			tableContent += '<td colSpan="' + colClsColSpan + '">' + colClsNames + '</td></tr>';


			// add header rows to output buffer and close rows tr tag
			for (i = 0; i <= headerDepth - 1; i++) {
				tableContent += header[i] + '</tr>';
			}

			// generate body rows (leaves and sub totals)
			var tableBody = this.parseFlatBody(colData.treeData, treeDepth, colId, 0, colData.columns[0][0][0].children[0].children[0].renderCell, "", []);

			// generate date row
			var date = _.escape(locale.resource("SLICER_DATA_DATE", [slicer.store.getDate()]));
			date = '<tr><td colspan="' + totalColSpan + '">' + date + '</td></tr>';

			cssCache = {}; // clear cache, return used memory

			return tableContent + tableBody + date + "</table>";
		},

		/**
		 * @override
		 * Submit to the server for the processing
		 */
		publish: function() {
			const cfg = this.widget._slicer._config;

			// switch state to indicate excel export in progress in case rendering strategy needs to change
			state.set("excel", true);

			var output = '<html>' +
				'<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />' +
				'<body>' +
				(cfg.flatSlicer ? this._createFlatTable() : this._createTable()) +
				'</body>' +
				'</html>';
			const viewName = this.widget.defView.getName();

			// Excel rendering done, we don't need this state anymore
			state.set("excel", false);

			var form = new FormData();

			form.append("Module", "SiteAdmin");
			form.append("Operation", "ConvertHTMLToExcel");
			form.append("Name", viewName);
			form.append("Content", output);

			_.each(this._getLogParams(), function(value, key) {
				if (value != null) {
					form.append(key, value);
				}
			});

			this._submitFormData(form, viewName);

			// continue with post-processing
			Controller.prototype.publish.call(this);
		},

		/**
		 * Submit FormData
		 * @param {FormData} form
		 * @param {string} viewName
		 * @return {Deferred}
		 */
		_submitFormData: function(form, viewName) {
			var ioArgs = {
				headers: {},
				data: form,
				handleAs: "blob"
			};

			return new PostRequest().post(ioArgs).then(function(response) {
				var filename;

				// parse 'Content-Disposition' header to get to the filename
				var contentDisposition = response.getHeader('Content-Disposition');
				if (contentDisposition) {
					var stringToSearch = 'filename="';
					var startIndex = contentDisposition.indexOf(stringToSearch);
					if (startIndex >= 0) {
						startIndex += stringToSearch.length;
						var endIndex = contentDisposition.indexOf('"', startIndex);
						if (endIndex >= 0) {
							filename = contentDisposition.substring(startIndex, endIndex);
						} else {
							filename = contentDisposition.substring(startIndex);
						}

						filename = decodeURIComponent(filename);
					}
				}
				html.download(filename || (viewName + '.xlsx'), response.data);
			});
		},

		/**
		 * @override
		 * Finalization
		 */
		postProcess: function(bStatus) {
			this.setProgress(null);
			this.inherited(arguments);
			html.showWait(false);
		}
	});
});